import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../constants/routes';

const COOKIE_STORAGE_KEY = 'tendotec_cookie_consent_v1';

/** WhatsApp chat link (E.164 without +) */
export const WHATSAPP_E164 = '436766999888';
const whatsappHref = `https://wa.me/${WHATSAPP_E164}`;

const SiteWidgets: React.FC = () => {
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_STORAGE_KEY);
      setShowCookieBanner(!stored);
    } catch {
      setShowCookieBanner(true);
    }
  }, []);

  const saveConsent = useCallback((value: 'accepted' | 'rejected') => {
    try {
      localStorage.setItem(COOKIE_STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
    setShowCookieBanner(false);
  }, []);

  return (
    <>
      {showCookieBanner && (
        <section
          className="cookie-banner"
          aria-label="Cookie-Hinweis"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-desc"
        >
          <div className="cookie-banner__inner">
            <div className="cookie-banner__icon" aria-hidden="true">
              <i className="fa-solid fa-cookie-bite" />
            </div>
            <div className="cookie-banner__text">
              <h2 id="cookie-banner-title" className="cookie-banner__title">
                Cookies &amp; Privatsphäre
              </h2>
              <p id="cookie-banner-desc" className="cookie-banner__desc">
                Wir verwenden Cookies, um unsere Website nutzerfreundlich zu gestalten und zu verbessern. Mit „Alle akzeptieren“
                willigen Sie in die Verwendung ein. Details finden Sie in unserer{' '}
                <Link to={ROUTES.DATENSCHUTZ} className="cookie-banner__link">
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button type="button" className="cookie-banner__btn cookie-banner__btn--ghost" onClick={() => saveConsent('rejected')}>
                Ablehnen
              </button>
              <button type="button" className="cookie-banner__btn cookie-banner__btn--primary" onClick={() => saveConsent('accepted')}>
                Alle akzeptieren
              </button>
            </div>
          </div>
        </section>
      )}

      <a
        href={whatsappHref}
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp: TendoTec kontaktieren"
        title="WhatsApp"
        style={{ bottom: showCookieBanner ? '6.75rem' : '1.25rem' }}
      >
        <i className="fa-brands fa-whatsapp" aria-hidden="true" />
      </a>

      <style>{`
        .cookie-banner {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10050;
          padding: 1rem max(1rem, env(safe-area-inset-right)) calc(1rem + env(safe-area-inset-bottom))
            max(1rem, env(safe-area-inset-left));
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.97) 0%, #ffffff 100%);
          border-top: 1px solid var(--border, #e5e7eb);
          box-shadow: 0 -12px 40px rgba(15, 23, 42, 0.12);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }
        .cookie-banner__inner {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem 1.25rem;
        }
        .cookie-banner__icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: var(--accent-light, rgba(37, 99, 235, 0.1));
          color: var(--accent, #2563eb);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.35rem;
          flex-shrink: 0;
        }
        .cookie-banner__text {
          flex: 1;
          min-width: min(100%, 280px);
        }
        .cookie-banner__title {
          margin: 0 0 0.35rem;
          font-size: 1.05rem;
          font-weight: 700;
          color: var(--text-main, #111827);
        }
        .cookie-banner__desc {
          margin: 0;
          font-size: 0.88rem;
          line-height: 1.55;
          color: var(--text-muted, #6b7280);
        }
        .cookie-banner__link {
          color: var(--accent, #2563eb);
          font-weight: 600;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .cookie-banner__link:hover {
          color: #1d4ed8;
        }
        .cookie-banner__actions {
          display: flex;
          flex-wrap: wrap;
          gap: 0.6rem;
          align-items: center;
          flex-shrink: 0;
        }
        .cookie-banner__btn {
          border-radius: 999px;
          padding: 0.65rem 1.25rem;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: inherit;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .cookie-banner__btn--primary {
          background: linear-gradient(135deg, #111827, #374151);
          color: #fff;
          box-shadow: 0 4px 14px rgba(17, 24, 39, 0.2);
        }
        .cookie-banner__btn--primary:hover {
          transform: translateY(-1px);
        }
        .cookie-banner__btn--ghost {
          background: #fff;
          color: var(--text-main, #111827);
          border: 2px solid var(--border, #e5e7eb);
        }
        .cookie-banner__btn--ghost:hover {
          border-color: var(--accent, #2563eb);
          color: var(--accent, #2563eb);
        }
        @media (max-width: 640px) {
          .cookie-banner__inner {
            flex-direction: column;
            align-items: stretch;
          }
          .cookie-banner__actions {
            width: 100%;
            flex-direction: column;
          }
          .cookie-banner__btn {
            width: 100%;
            justify-content: center;
          }
        }

        .whatsapp-float {
          position: fixed;
          right: max(1rem, env(safe-area-inset-right));
          z-index: 10040;
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #25d366;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.85rem;
          box-shadow: 0 8px 28px rgba(37, 211, 102, 0.45);
          text-decoration: none;
          transition: bottom 0.35s ease, transform 0.2s ease, box-shadow 0.2s ease;
        }
        .whatsapp-float:hover {
          transform: scale(1.06);
          box-shadow: 0 12px 32px rgba(37, 211, 102, 0.55);
        }
        .whatsapp-float:focus-visible {
          outline: 3px solid #fff;
          outline-offset: 3px;
        }
      `}</style>
    </>
  );
};

export default SiteWidgets;
